import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Card, Empty, Spin, Tooltip, TreeSelect, Typography } from "antd";
import { QuestionCircleOutlined, TagOutlined } from "@ant-design/icons";
import { Toast } from "../../../ui";
import SynchronizationService from "../../../../services/synchronizationService";
import { useTheme } from "../../../../contexts/ThemeContext";
import {
  OPERATIONS,
  buildCategoryTree,
  normalizeCode,
} from "./DefaultFinancialCategoriesModal";

const { Text } = Typography;

const DefaultFinancialCategoriesConfig = forwardRef(function DefaultFinancialCategoriesConfig({ syncId, onSaved, onConfigChange }, ref) {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [mappings, setMappings] = useState({});
  const [initialMappings, setInitialMappings] = useState({});

  const treeData = useMemo(() => buildCategoryTree(categories), [categories]);

  const flatByCode = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((c) => {
      const code = normalizeCode(c?.codCategoria);
      if (code) map.set(code, c);
    });
    return map;
  }, [categories]);

  const isDirty = useMemo(() => {
    const keys = new Set([
      ...Object.keys(mappings || {}),
      ...Object.keys(initialMappings || {}),
    ]);
    for (const k of keys) {
      const a = mappings?.[k];
      const b = initialMappings?.[k];
      if ((a?.codCategoria || "") !== (b?.codCategoria || "")) return true;
    }
    return false;
  }, [mappings, initialMappings]);

  const loadAll = useCallback(async () => {
    if (!syncId) return;
    setLoading(true);
    try {
      const [cats, current] = await Promise.all([
        SynchronizationService.getSynchronizationCategoryList(syncId),
        SynchronizationService.getDefaultFinancialCategories(syncId),
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      const next = {};
      (current || []).forEach((row) => {
        const op =
          typeof row.operation === "number"
            ? row.operation
            : Number(row.operation);
        if (Number.isFinite(op)) {
          next[op] = {
            id: row.id ?? null,
            codCategoria: normalizeCode(row.codCategoria),
          };
        }
      });
      setMappings(next);
      setInitialMappings(JSON.parse(JSON.stringify(next)));
    } catch (e) {
      console.error(e);
      Toast.error("Não foi possível carregar a configuração.");
    } finally {
      setLoading(false);
    }
  }, [syncId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (onConfigChange) {
      const items = OPERATIONS.map((op) => {
        const current = mappings[op.id] || {};
        return {
          id: current.id ?? null,
          operation: op.id,
          codCategoria: current.codCategoria || "",
          isDeleted: !current.codCategoria,
        };
      });
      onConfigChange({
        mappings,
        isDirty,
        saveItems: items,
      });
    }
  }, [mappings, isDirty, onConfigChange]);

  const handleChange = useCallback((operationId, codCategoria) => {
    setMappings((prev) => ({
      ...prev,
      [operationId]: {
        ...(prev[operationId] || {}),
        codCategoria: normalizeCode(codCategoria),
      },
    }));
  }, []);

  const markAsSaved = useCallback(() => {
    setInitialMappings(JSON.parse(JSON.stringify(mappings)));
    onSaved?.();
  }, [mappings, onSaved]);

  useImperativeHandle(ref, () => ({
    markAsSaved,
  }), [markAsSaved]);

  const renderRow = (op) => {
    const value = mappings[op.id]?.codCategoria || undefined;
    const selectedCat = value ? flatByCode.get(value) : null;
    const displayValue = selectedCat ? value : value || undefined;

    return (
      <div
        key={op.id}
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(220px, 1fr) minmax(260px, 1.2fr)",
          alignItems: "center",
          gap: 16,
          padding: "10px 12px",
          borderBottom: `1px solid ${theme.borderSecondary}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Text style={{ color: theme.textPrimary }}>{op.label}</Text>
          <Tooltip title={op.help}>
            <QuestionCircleOutlined
              style={{ color: theme.textSecondary, fontSize: 14 }}
            />
          </Tooltip>
        </div>
        <TreeSelect
          allowClear
          showSearch
          autoClearSearchValue={false}
          treeDefaultExpandAll={false}
          placeholder={op.label}
          value={displayValue}
          treeData={treeData}
          treeNodeFilterProp="title"
          style={{ width: "100%" }}
          onChange={(val) => handleChange(op.id, val)}
          dropdownStyle={{ maxHeight: 360, overflow: "auto" }}
          notFoundContent={
            loading ? (
              <Spin size="small" />
            ) : (
              <Empty
                description="Nenhuma categoria"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }
        />
      </div>
    );
  };

  return (
    <Card
      size="small"
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <TagOutlined />
          <Text strong style={{ color: theme.textPrimary }}>
            Categorias financeiras padrão
          </Text>
        </div>
      }
      style={{
        backgroundColor: theme.background,
        borderColor: theme.border,
      }}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          gap: 8,
        },
      }}
    >
      <Text style={{ color: theme.textSecondary }}>
        Vincule cada operação financeira a uma categoria do seu plano de contas.
      </Text>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(220px, 1fr) minmax(260px, 1.2fr)",
          gap: 16,
          padding: "8px 12px",
          backgroundColor: theme.borderSecondary,
          borderRadius: 6,
          fontWeight: 600,
        }}
      >
        <Text style={{ color: theme.textPrimary }}>Operação financeira</Text>
        <Text style={{ color: theme.textPrimary }}>
          Vincular automaticamente à categoria
        </Text>
      </div>
      <Spin spinning={loading}>
        <div style={{ marginTop: 4 }}>{OPERATIONS.map(renderRow)}</div>
      </Spin>
    </Card>
  );
});

export default DefaultFinancialCategoriesConfig;
